import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './SessionView.css';

function SessionView() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [clinicalNote, setClinicalNote] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSessionData = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`http://localhost:5000/api/sessions/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSession(data.session);
        setClinicalNote(data.clinical_note);
        setEntities(data.entities || []);
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSessionData();
  }, [fetchSessionData]);

  const handleApprove = async () => {
    if (!clinicalNote) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`http://localhost:5000/api/notes/${clinicalNote.id}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Note approved successfully!');
      fetchSessionData();
    } catch (error) {
      alert('Failed to approve note');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!session) return <div className="error">Session not found</div>;

  return (
    <div className="session-view-container">
      <div className="session-header">
        <button onClick={() => navigate('/')} className="btn-back">← Back to Dashboard</button>
        <h1>Clinical Session Details</h1>
      </div>

      <div className="session-info-card">
        <h2>Session Information</h2>
        <div className="info-grid">
          <div><strong>Patient:</strong> {session.patient_first_name} {session.patient_last_name}</div>
          <div><strong>Date:</strong> {new Date(session.created_at).toLocaleString()}</div>
          <div><strong>Duration:</strong> {session.duration_seconds ? `${Math.floor(session.duration_seconds / 60)}m` : 'N/A'}</div>
          <div><strong>Status:</strong> {session.status}</div>
        </div>
      </div>

      {clinicalNote && (
        <div className="clinical-note-card">
          <h2>SOAP Note</h2>
          
          <div className="soap-section">
            <h3>Chief Complaint</h3>
            <p>{clinicalNote.chief_complaint || 'N/A'}</p>
          </div>

          <div className="soap-section">
            <h3>Subjective</h3>
            <p className="pre-formatted">{clinicalNote.subjective || 'N/A'}</p>
          </div>

          <div className="soap-section">
            <h3>Objective</h3>
            <p className="pre-formatted">{clinicalNote.objective || 'N/A'}</p>
          </div>

          <div className="soap-section">
            <h3>Assessment</h3>
            <p className="pre-formatted">{clinicalNote.assessment || 'N/A'}</p>
          </div>

          <div className="soap-section">
            <h3>Plan</h3>
            <p className="pre-formatted">{clinicalNote.plan || 'N/A'}</p>
          </div>

          {clinicalNote.red_flags && (
            <div className="soap-section alert-section">
              <h3>⚠️ Red Flags</h3>
              <p>{clinicalNote.red_flags}</p>
            </div>
          )}

          {!clinicalNote.approved && (
            <button onClick={handleApprove} className="btn-approve">
              ✓ Approve Note
            </button>
          )}
          {clinicalNote.approved && (
            <div className="approved-badge">✓ Approved</div>
          )}
        </div>
      )}

      {entities.length > 0 && (
        <div className="entities-card">
          <h2>Extracted Entities</h2>
          <div className="entities-grid">
            {entities.map((entity, index) => (
              <div key={index} className="entity-item">
                <span className="entity-type">{entity.entity_type}</span>
                <span className="entity-value">{entity.entity_value}</span>
                <span className="entity-confidence">{(entity.confidence_score * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionView;
